"use client";

import { LanguageSwitcher } from "@/component/language-switcher";
import { Link } from "@/component/link";
import Logo from "@/component/logo";
import { useLanguage } from "@/context/language-context";
import { useTranslations } from "@/hooks/use-translations";
import { getLocalizedPath, routes } from "@/lib/routes";
import { Config } from "@/shared/config";
import { IconBrandInstagramFilled, IconBrandLinkedinFilled } from "@tabler/icons-react";
import { useMemo } from "react";

export default function Footer() {
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const { language } = useLanguage();
  const t = useTranslations();

  if (!t) return null;

  return (
    <footer className="static block w-full pb-8 desktop:pb-16 px-4 desktop:px-0">
      <div className="flex justify-center">
        <div className="inline-grid grid-cols-2 desktop:grid-cols-2 gap-12">
          <div className="flex flex-col gap-3 items-start desktop:border-1 border-white/10 dark:border-white/10 rounded-lg p-4 desktop:p-4">
            <Logo />

            <p className="text-sm max-w-xs">
              {t.footer.description}
            </p>

            <Link
              href={getLocalizedPath('/', language)}
              className="text-sm">
              {t.footer.navigation.home}
            </Link>
            <Link href="https://ricu.it" target="_blank" className="text-sm">
              {t.footer.navigation.aboutMe}
            </Link>
            <Link href={routes.requestInfo[language]} className="text-sm">
              {t.navbar.preRegistration}
            </Link>
            {/* <Link href="/mailing-list" className="text-sm">
              {t.footer.navigation.mailingList}
            </Link>
            <Link href="/contact" className="text-sm">
              {t.footer.navigation.contact}
            </Link> */}
          </div>

          <div className="flex flex-col items-end gap-3 desktop:border-1 border-white/10 dark:border-white/10 rounded-lg p-4 desktop:p-4 desktop:hidden">
            <span className="text-sm font-medium">{t.footer.legal.title}</span>
            <Link
              href="/docs/privacy-policy"
              target="_blank"
              className="text-sm"
            >
              {t.footer.legal.privacy}
            </Link>
            <Link
              href="/docs/terms-of-service"
              target="_blank"
              className="text-sm"
            >
              {t.footer.legal.terms}
            </Link>
            <Link
              href="/docs/cookie-policy"
              target="_blank"
              className="text-sm"
            >
              {t.footer.legal.cookies}
            </Link>

            <div className="mt-6 flex flex-col items-end gap-3 desktop:hidden text-end">
              <p className="text-sm font-medium">{t.footer.alternatives.title}</p>
              <div className="flex flex-col items-end gap-2">
                <Link
                  href="/alternative/coworking-varese-next"
                  className="text-sm hover:text-primary transition-colors"
                >
                  {t.footer.alternatives.vareselabVsVaresenext}
                </Link>
                <Link
                  href="/alternative/opificio-42-coworking"
                  className="text-sm hover:text-primary transition-colors"
                >
                  {t.footer.alternatives.vareselabVsOpificio42}
                </Link>
              </div>
            </div>

            <div className="mt-6 flex flex-col items-end gap-3 desktop:hidden text-end">
              <p className="text-sm font-medium">{t.footer.social.title}</p>
              <div className="flex gap-4">
                <Link
                  href="https://www.instagram.com/vareselab"
                  aria-label="Instagram"
                >
                  <IconBrandInstagramFilled />
                </Link>
                <Link
                  href="https://www.linkedin.com/in/matteo-ricupero-707abb121/"
                  aria-label={t.footer.social.linkedin}
                >
                  <IconBrandLinkedinFilled />
                </Link>
              </div>
            </div>
          </div>

          <div className="hidden desktop:flex desktop:items-start desktop:gap-10 desktop:border-1 border-white/10 dark:border-white/10 rounded-lg p-4 desktop:p-4">
            <div className="flex flex-col items-end gap-3">
              <p className="text-sm font-medium">{t.footer.social.title}</p>
              <div className="flex gap-4">
                <Link
                  href="https://www.instagram.com/vareselab"
                  aria-label="Instagram"
                >
                  <IconBrandInstagramFilled />
                </Link>
                <Link
                  href="https://www.linkedin.com/in/matteo-ricupero-707abb121/"
                  aria-label={t.footer.social.linkedin}
                >
                  <IconBrandLinkedinFilled />
                </Link>
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <p className="text-sm font-medium">{t.footer.alternatives.title}</p>
              <div className="flex flex-col items-end gap-2">
                <Link
                  href="/alternative/coworking-varese-next"
                  className="text-sm hover:text-primary transition-colors"
                >
                  {t.footer.alternatives.vareselabVsVaresenext}
                </Link>
                <Link
                  href="/alternative/opificio-42-coworking"
                  className="text-sm hover:text-primary transition-colors"
                >
                  {t.footer.alternatives.vareselabVsOpificio42}
                </Link>
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <span className="text-sm font-medium">{t.footer.legal.title}</span>
              <Link
                href="/docs/privacy-policy"
                target="_blank"
                className="text-sm"
              >
                {t.footer.legal.privacy}
              </Link>
              <Link
                href="/docs/terms-of-service"
                target="_blank"
                className="text-sm"
              >
                {t.footer.legal.terms}
              </Link>
              <Link
                href="/docs/cookie-policy"
                target="_blank"
                className="text-sm"
              >
                {t.footer.legal.cookies}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-6 text-center">
        <div className="flex justify-center mb-4">
          <LanguageSwitcher />
        </div>
        <p className="text-sm opacity-70">
          © {currentYear} {Config.projectName}. {t.footer.copyright}
        </p>
        <p className="text-sm opacity-70 mt-1">
          Icons by <a href="https://www.thiings.co" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Thiings.co</a> and <a href="https://lucide.dev" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Lucide</a>
        </p>
      </div>
    </footer>
  );
}
